import { Component, EventEmitter, OnInit } from '@angular/core';
import { AccountTypeService } from '../account-type.service';
import { AccountType } from '../account-type';
import { NotificationService } from '../../../shared/notification.service';

@Component({
  selector: 'app-account-type-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class AccountTypeListComponent implements OnInit {
  public accountType: AccountType;

  /**
   * A list of all the account-types currently loaded.
   *
   * @type {AccountType[]}
   */
  public accountTypes: AccountType[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of account-types.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of account-types that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of account-types that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a accountType has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public accountTypeDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {AccountTypeService} accountTypeService
   * @param {NotificationService} notificationService
   */
  constructor(
    private accountTypeService: AccountTypeService,
    private notificationService: NotificationService,
  ) {
    this.accountTypeService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.accountTypeService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.accountTypeService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.accountTypeService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the accountType list.
   */
  public ngOnInit(): void {
    this.loadAccountTypes(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * accountType list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadAccountTypes(pageNumber);
  }

  /**
   * Loads a list of account-types using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadAccountTypes(page?: number, limit?: number): void {
    const listAccountTypes = this.accountTypeService
      .listAccountTypes(page, limit)
      .subscribe((accountTypes: AccountType[]) => {
        this.accountTypes = accountTypes;
        listAccountTypes.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate accountType ID
   * to delete the accountType.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.accountTypeService.deleteAccountType(id).subscribe(
      deleted => {
        this.notificationService.success(
          `AccountType Deleted`,
          `AccountType with ID ${id} was deleted.`,
          5,
        );
        this.accountTypeDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `AccountType Not Deleted`,
          err.error.message,
          10,
        );
        this.accountTypeDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
